import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Calendar, Clock, User, Zap, Phone, MessageCircle } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const Blog = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // SEO: Set canonical to avoid duplicate content issues
  useSEO({
    title: 'Blog ArrancaYa BCN - Consejos Batería Coche Barcelona | Arranque Profesional',
    description: 'Guías completas sobre batería del coche descargada en Barcelona. Aprende cómo arrancar tu coche con pinzas, prevenir descargas y cuándo llamar a ArrancaYa BCN para arranque profesional 24/7.',
    canonical: 'https://arrancaya-bcn.es/blog'
  });

  useEffect(() => {
    // Schema markup para blog
    const schema = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Blog ArrancaYa BCN",
      "description": "Consejos y guías sobre batería de coche y arranque en Barcelona",
      "url": "https://arrancaya-bcn.es/blog",
      "publisher": {
        "@type": "Organization",
        "name": "ArrancaYa BCN",
        "url": "https://arrancaya-bcn.es",
        "logo": "https://arrancaya-bcn.es/images/logo.png"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // GTM tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Blog ArrancaYa BCN',
      'page_location': window.location.href,
      'content_group1': 'Blog'
    });

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const blogArticles = [
    {
      id: 'arrancar-coche-con-pinzas',
      title: 'Cómo Arrancar el Coche con Pinzas Paso a Paso',
      excerpt: 'Guía completa para arrancar tu coche con cables de arranque. Tutorial profesional paso a paso con todas las precauciones de seguridad.',
      image: '',
      category: 'Tutorial',
      readTime: '7 min',
      date: '2026-01-15',
      keywords: ['arrancar coche con pinzas', 'cables de arranque', 'batería descargada'],
      featured: true
    },
    {
      id: 'bateria-descargada-coche-alquiler',
      title: 'Batería Descargada en Coche de Alquiler: Qué Hacer',
      excerpt: '¿Tu coche de alquiler no arranca en Barcelona? Guía paso a paso para resolver el problema sin complicaciones con la empresa de alquiler.',
      image: '',
      category: 'Guía Práctica',
      readTime: '6 min',
      date: '2026-01-20',
      keywords: ['coche alquiler', 'batería descargada', 'rental car Barcelona'],
      featured: true
    },
    {
      id: 'como-saber-bateria-muerta',
      title: '¿Batería Muerta o Solo Descargada? Cómo Saberlo',
      excerpt: 'Aprende a distinguir entre una batería descargada (se puede arrancar) y una batería muerta (necesita cambio). Test rápido incluido.',
      image: '',
      category: 'Diagnóstico',
      readTime: '5 min',
      date: '2026-01-20',
      keywords: ['batería muerta', 'diagnóstico batería', 'cambiar batería'],
      featured: true
    },
    {
      id: 'precio-cambio-bateria-barcelona',
      title: 'Precio Cambio Batería Coche Barcelona 2026',
      excerpt: 'Guía completa de precios para cambiar la batería del coche en Barcelona. Dónde hacerlo, cuánto cuesta y alternativas más económicas.',
      image: '',
      category: 'Precios 2026',
      readTime: '6 min',
      date: '2026-01-20',
      keywords: ['precio batería coche', 'cambiar batería Barcelona', 'cuánto cuesta'],
      featured: false
    },
    {
      id: 'sintomas-bateria-descargada',
      title: 'Síntomas de Batería Descargada: Cómo Identificarlos',
      excerpt: 'Aprende a reconocer las señales tempranas antes de quedarte tirado. Diagnóstico profesional de problemas de batería con tests caseros.',
      image: '',
      category: 'Diagnóstico',
      readTime: '5 min',
      date: '2026-01-14',
      keywords: ['síntomas batería descargada', 'diagnóstico coche', 'problemas batería'],
      featured: false
    },
    {
      id: 'arrancar-empujando-coche',
      title: '¿Se Puede Arrancar Empujando el Coche? Guía Completa',
      excerpt: 'Descubre cuándo es posible arrancar empujando, los riesgos reales y por qué es mejor llamar a un servicio profesional.',
      image: '',
      category: 'Consejos',
      readTime: '4 min',
      date: '2026-01-13',
      keywords: ['arrancar empujando', 'arranque por empuje', 'coche manual'],
      featured: false
    },
    {
      id: 'que-hacer-despues-arrancar',
      title: 'Qué Hacer Después de Arrancar con la Batería Descargada',
      excerpt: 'Pasos críticos tras un arranque de emergencia. Evita quedarte tirado otra vez siguiendo estos consejos profesionales.',
      image: '',
      category: 'Mantenimiento',
      readTime: '3 min',
      date: '2026-01-12',
      keywords: ['después de arrancar', 'batería descargada', 'mantenimiento'],
      featured: false
    },
    {
      id: 'prevenir-bateria-descargada',
      title: 'Cómo Prevenir que la Batería se Descargue',
      excerpt: 'Tips profesionales para evitar quedarte sin batería. Mantenimiento preventivo que te ahorrará disgustos y hasta 300€.',
      image: '',
      category: 'Prevención',
      readTime: '6 min',
      date: '2026-01-11',
      keywords: ['prevenir batería descargada', 'mantenimiento batería', 'cuidado coche'],
      featured: false
    }
  ];

  const filteredArticles = blogArticles.filter(article =>
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const featuredArticles = blogArticles.filter(article => article.featured);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header del Blog */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Blog ArrancaYa BCN
            </h1>
            <p className="text-xl md:text-2xl mb-6 opacity-90">
              Todo sobre <strong>batería del coche descargada</strong> y arranque en Barcelona
            </p>
            <p className="text-lg opacity-80 max-w-3xl mx-auto">
              Guías profesionales, consejos de mantenimiento y soluciones rápidas 
              para problemas de batería. Aprende de nuestros expertos o llámanos 24/7.
            </p>
          </div>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-md mx-auto">
          <input
            type="text"
            placeholder="Buscar artículos sobre batería, arranque..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Artículos Destacados */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Artículos Destacados</h2>
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {featuredArticles.map((article) => (
            <div key={article.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-200">
              {/* Header coloré selon la catégorie */}
              <div className={`p-6 ${
                article.category === 'Tutorial' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                article.category === 'Diagnóstico' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                article.category === 'Consejos' ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                'bg-gradient-to-r from-blue-500 to-blue-600'
              }`}>
                <div className="flex items-center justify-between text-white mb-4">
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                    {article.category}
                  </span>
                  <div className="flex items-center text-sm">
                    <Clock className="h-4 w-4 mr-1" />
                    {article.readTime}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 leading-tight">
                  {article.title}
                </h3>
              </div>
              
              {/* Contenu */}
              <div className="p-6">
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(article.date).toLocaleDateString('es-ES')}
                </div>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {article.excerpt}
                </p>
                
                {/* Tags de mots-clés */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {article.keywords.slice(0, 2).map((keyword) => (
                    <span key={keyword} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                      {keyword}
                    </span>
                  ))}
                </div>
                
                <Link 
                  to={`/blog/${article.id}`}
                  className="inline-flex items-center font-semibold text-blue-600 hover:text-blue-800 transition-colors group"
                >
                  Leer artículo completo 
                  <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Todos los Artículos */}
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Todos los Artículos</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArticles.map((article) => (
            <div key={article.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-blue-300">
              {/* Header de categoría */}
              <div className={`p-4 ${
                article.category === 'Tutorial' ? 'bg-green-50 border-b border-green-100' :
                article.category === 'Diagnóstico' ? 'bg-purple-50 border-b border-purple-100' :
                article.category === 'Consejos' ? 'bg-orange-50 border-b border-orange-100' :
                article.category === 'Mantenimiento' ? 'bg-blue-50 border-b border-blue-100' :
                article.category === 'Prevención' ? 'bg-green-50 border-b border-green-100' :
                'bg-gray-50 border-b border-gray-100'
              }`}>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    article.category === 'Tutorial' ? 'bg-green-200 text-green-800' :
                    article.category === 'Diagnóstico' ? 'bg-purple-200 text-purple-800' :
                    article.category === 'Consejos' ? 'bg-orange-200 text-orange-800' :
                    article.category === 'Mantenimiento' ? 'bg-blue-200 text-blue-800' :
                    article.category === 'Prevención' ? 'bg-green-200 text-green-800' :
                    'bg-gray-200 text-gray-800'
                  }`}>
                    {article.category}
                  </span>
                  <span className="text-xs text-gray-500">{article.readTime}</span>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="font-bold text-gray-900 mb-3 line-clamp-2 text-lg leading-tight">
                  {article.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                  {article.excerpt}
                </p>
                
                {/* Keywords tags */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {article.keywords.slice(0, 3).map((keyword) => (
                    <span key={keyword} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                      {keyword.length > 15 ? keyword.substring(0, 15) + '...' : keyword}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {new Date(article.date).toLocaleDateString('es-ES')}
                  </span>
                  <Link 
                    to={`/blog/${article.id}`}
                    className="text-blue-600 font-semibold text-sm hover:text-blue-800 transition-colors group"
                  >
                    Leer artículo 
                    <ArrowRight className="h-3 w-3 ml-1 inline transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA para el servicio */}
      <div className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Zap className="h-16 w-16 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">
            ¿Necesitas Ayuda con tu Batería Ahora?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            No te compliques. Llamanos y te arrancamos el coche en Barcelona en menos de 30 minutos.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/')}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg"
            >
              <Zap className="h-5 w-5 mr-2" />
              Solicitar Arranque Ahora
            </Button>
            <Button
              onClick={() => window.open('tel:+34613076099')}
              variant="outline"
              className="bg-white text-blue-600 border-2 border-white hover:bg-blue-50 px-8 py-4 text-lg"
            >
              <Phone className="h-5 w-5 mr-2" />
              Llamar: 613 076 099
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;